import { create } from 'zustand';

/* ================================================================== */
/*  Wallet Store — MetaMask / injected provider connection            */
/*  Uses ethers.js v6 BrowserProvider                                 */
/* ================================================================== */

interface WalletState {
  address: string | null;
  chainId: number | null;
  balance: string | null;
  isConnecting: boolean;
  isConnected: boolean;
  error: string | null;

  connect: () => Promise<void>;
  disconnect: () => void;
  switchChain: (chainId: number) => Promise<void>;
  refreshBalance: () => Promise<void>;
}

/* helpers — lazy-import ethers to keep initial bundle lean */
async function getEthers() {
  const { ethers } = await import('ethers');
  return ethers;
}

function getEthereum(): unknown {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return typeof window !== 'undefined' ? (window as any).ethereum : undefined;
}

export const useWalletStore = create<WalletState>((set, get) => {
  /* ---- event listeners (attached once on first connect) ---- */
  let listenersAttached = false;

  function attachListeners() {
    const ethereum = getEthereum() as any; // eslint-disable-line @typescript-eslint/no-explicit-any
    if (!ethereum || listenersAttached) return;
    listenersAttached = true;

    ethereum.on?.('accountsChanged', (accounts: string[]) => {
      if (accounts.length === 0) {
        set({ address: null, balance: null, isConnected: false, chainId: null });
      } else {
        set({ address: accounts[0] });
        get().refreshBalance();
      }
    });

    ethereum.on?.('chainChanged', (chainIdHex: string) => {
      const chainId = parseInt(chainIdHex, 16);
      set({ chainId });
      get().refreshBalance();
    });

    ethereum.on?.('disconnect', () => {
      set({ address: null, balance: null, isConnected: false, chainId: null });
    });
  }

  return {
    address: null,
    chainId: null,
    balance: null,
    isConnecting: false,
    isConnected: false,
    error: null,

    connect: async () => {
      const ethereum = getEthereum() as any; // eslint-disable-line @typescript-eslint/no-explicit-any
      if (!ethereum) {
        set({ error: 'No wallet detected. Install MetaMask or another Web3 wallet.' });
        return;
      }

      set({ isConnecting: true, error: null });

      try {
        const ethers = await getEthers();
        const provider = new ethers.BrowserProvider(ethereum);
        const accounts = await provider.send('eth_requestAccounts', []);
        const network = await provider.getNetwork();
        const address = accounts[0] as string;
        const balanceWei = await provider.getBalance(address);

        attachListeners();

        set({
          address,
          chainId: Number(network.chainId),
          balance: ethers.formatEther(balanceWei),
          isConnected: true,
          isConnecting: false,
          error: null,
        });
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to connect wallet';
        set({ isConnecting: false, error: message });
      }
    },

    disconnect: () => {
      set({
        address: null,
        chainId: null,
        balance: null,
        isConnected: false,
        error: null,
      });
    },

    switchChain: async (chainId: number) => {
      const ethereum = getEthereum() as any; // eslint-disable-line @typescript-eslint/no-explicit-any
      if (!ethereum) return;

      const hexChainId = '0x' + chainId.toString(16);
      try {
        await ethereum.request?.({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: hexChainId }],
        });
        set({ chainId });
        get().refreshBalance();
      } catch (err: unknown) {
        // 4902 = chain not added — could auto-add here in the future
        const message = err instanceof Error ? err.message : 'Failed to switch chain';
        set({ error: message });
      }
    },

    refreshBalance: async () => {
      const { address } = get();
      const ethereum = getEthereum() as any; // eslint-disable-line @typescript-eslint/no-explicit-any
      if (!address || !ethereum) return;

      try {
        const ethers = await getEthers();
        const provider = new ethers.BrowserProvider(ethereum);
        const balanceWei = await provider.getBalance(address);
        set({ balance: ethers.formatEther(balanceWei) });
      } catch {
        // silently ignore balance refresh errors
      }
    },
  };
});
